export const GENERATION_MODES = [
  'text2img',
  'img2img',
  'edit',
  'restore',
  'tryon',
] as const;

export type GenerationMode = (typeof GENERATION_MODES)[number];
export type GenerateSourceEntry = 'generate_page' | 'chat_result' | 'result_regenerate';
export type GenerationOperation = 'image_generate' | 'image_edit';

const GENERATE_ENTRY_BY_MODE: Record<GenerationMode, string> = {
  text2img: 'generate_text2img',
  img2img: 'generate_img2img',
  edit: 'generate_edit',
  restore: 'generate_restore',
  tryon: 'generate_tryon',
};

const MODEL_ENTRY_ALIASES: Record<string, string> = {
  gpt: 'gpt_image_2',
  nanobanana: 'gemini_nano',
};

export interface GenerationEntryContext {
  mode: GenerationMode;
  prompt: string;
  styleId: string | null;
  ratio: string | null;
  modelId: string | null;
  category: string | null;
  templateId: string | null;
}

export function isGenerationMode(value: string | null): value is GenerationMode {
  return GENERATION_MODES.some((mode) => mode === value);
}

export function resolveGenerationEntryContext(
  params: Pick<URLSearchParams, 'get'>,
): GenerationEntryContext {
  const requestedMode = params.get('mode');
  const requestedModel = params.get('model');

  return {
    mode: isGenerationMode(requestedMode) ? requestedMode : 'text2img',
    prompt: params.get('prompt') ?? '',
    styleId: params.get('style') ?? params.get('styleId'),
    ratio: params.get('ratio'),
    modelId: requestedModel
      ? (MODEL_ENTRY_ALIASES[requestedModel] ?? requestedModel)
      : null,
    category: params.get('category'),
    templateId: params.get('template'),
  };
}

export function getGenerateEntryId(
  mode: string,
  sourceEntry?: GenerateSourceEntry,
): string {
  if (sourceEntry === 'chat_result') return 'chat_result';
  if (sourceEntry === 'result_regenerate') return 'result_regenerate';
  return isGenerationMode(mode)
    ? GENERATE_ENTRY_BY_MODE[mode]
    : `generate_${mode || 'unknown'}`;
}

export function getGenerationOperation(mode: string): GenerationOperation {
  return mode === 'text2img' ? 'image_generate' : 'image_edit';
}

export function getGenerationSelectionMode(
  mode: string,
  referenceImageCount: number,
): string {
  return mode === 'text2img' && referenceImageCount > 0 ? 'edit' : mode;
}

export function shouldResumeGenerationAfterPayment(
  rawPaySource: string | null,
  prompt: string,
): boolean {
  if (!rawPaySource) return false;

  try {
    const value = JSON.parse(rawPaySource) as { route?: unknown; action?: unknown };
    return value.route === '/generate'
      && value.action === 'gen_when_quota_empty'
      && prompt.trim().length > 0;
  } catch {
    return false;
  }
}
